import { FRESHA_BOOKING_URL } from "../config";
import { Activity, Sparkles, ScanFace, Check } from "lucide-react";
import { LegalLayout, LegalSection } from "../components/LegalLayout";

const HERO_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_216d3c35-aa83-4452-b36d-63c5224739d4/artifacts/22ssk5ie_WhatsApp%20Image%202026-08-24%20at%2020.25.21.jpeg";

const pillars = [
  {
    icon: Activity,
    title: "Hautstraffung",
    text: "Unterstützt die Kollagenneubildung und kann zu mehr Festigkeit und Elastizität der Haut beitragen.",
  },
  {
    icon: Sparkles,
    title: "Hautverjüngung",
    text: "Für ein glatteres, frischeres und vitaler wirkendes Hautbild.",
  },
  {
    icon: ScanFace,
    title: "Hautbild",
    text: "Kann feine Linien, eine raue Hautstruktur und vergrössert wirkende Poren optisch verbessern.",
  },
];

const suitable = [
  "Nachlassender Hautelastizität",
  "Feinen Linien und mimischen Fältchen",
  "Matter und müde wirkender Haut",
  "Rauer oder unregelmässiger Hautstruktur",
  "Vergrössert wirkenden Poren",
  "Reifer Haut",
];

const BookButton = ({ label, testId }) => (
  <a
    href={FRESHA_BOOKING_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    className="inline-block bg-[#1A1A1A] text-white px-8 py-4 rounded-full text-sm tracking-wider uppercase font-medium hover:bg-[#333] transition-colors duration-300"
  >
    {label}
  </a>
);

export default function LaserHautverjuengung() {
  return (
    <LegalLayout
      eyebrow="Behandlung"
      title="Laser Hautverjüngung & Hautstraffung"
      subtitle="Frischere, glattere und straffere Haut."
    >
      {/* Banner */}
      <LegalSection>
        <div
          className="relative overflow-hidden border border-[#E5E5E5] bg-[#1A1A1A]"
          data-testid="laser-hv-banner"
        >
          <img
            src={HERO_IMG}
            alt="Laser-Hautverjüngung mit dem Coolite Bolt Laser"
            style={{ objectPosition: "60% 88%" }}
            className="w-full h-[280px] md:h-[420px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1f4d52]/55 via-[#3d7d83]/15 to-transparent pointer-events-none" />
        </div>
      </LegalSection>

      {/* Intro */}
      <LegalSection heading="Frischere, glattere und straffere Haut">
        <p>
          Entdecken Sie eine moderne Laserbehandlung, die die natürlichen
          Regenerationsprozesse Ihrer Haut unterstützt.
        </p>
        <p>
          Die BOLT Laser-Hautverjüngung stimuliert durch gezielte Wärmeimpulse
          die Aktivität der Fibroblasten und unterstützt dadurch die Neubildung
          von Kollagen und Elastin.
        </p>
        <p>
          Das Ergebnis: Die Haut kann straffer, glatter und frischer wirken – für
          ein natürlich verjüngtes Erscheinungsbild, ohne invasive Eingriffe.
        </p>
        <div className="pt-2">
          <BookButton label="Termin buchen" testId="laser-hv-cta-top" />
        </div>
      </LegalSection>

      {/* Technology */}
      <LegalSection heading="Moderne Lasertechnologie für Ihre Haut">
        <p>
          Die BOLT-Technologie arbeitet mit den drei Wellenlängen 755 nm, 810 nm
          und 1064 nm und setzt kontrollierte Wärmeimpulse in unterschiedlichen
          Hauttiefen. Dadurch werden die natürlichen Regenerationsprozesse der
          Haut unterstützt und die Kollagen- und Elastinbildung angeregt.
        </p>
        <div className="grid sm:grid-cols-3 gap-6 mt-8" data-testid="laser-hv-pillars">
          {pillars.map((p) => (
            <div key={p.title} className="bg-white border border-[#E5E5E5] border-t-2 border-t-[#3d7d83] p-6">
              <div className="w-11 h-11 rounded-full bg-[#3d7d83]/12 flex items-center justify-center text-[#3d7d83] mb-4">
                <p.icon size={20} />
              </div>
              <h3 className="font-playfair text-lg text-[#1A1A1A] mb-2">{p.title}</h3>
              <p className="text-sm text-[#666666] leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
      </LegalSection>

      {/* Suitability */}
      <LegalSection heading="Für welche Haut ist die Behandlung geeignet?">
        <p className="mb-6">Die Laser-Hautverjüngung eignet sich besonders bei:</p>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="laser-hv-suitable">
          {suitable.map((s) => (
            <div key={s} className="flex gap-3 text-[#666666]">
              <Check size={20} className="text-[#C5A059] flex-shrink-0 mt-0.5" />
              <span className="leading-relaxed">{s}</span>
            </div>
          ))}
        </div>
      </LegalSection>

      {/* Regeneration */}
      <LegalSection heading="Natürliche Regeneration">
        <p>
          Die Wirkung entwickelt sich nicht nur unmittelbar nach der Behandlung.
          Durch die angeregten Regenerationsprozesse kann sich die Hautstruktur in
          den folgenden Wochen weiter verbessern und die Haut zunehmend straffer
          und elastischer wirken.
        </p>
        <p>
          Für ein optimales Ergebnis werden je nach Hautzustand in der Regel 3–5
          Behandlungen im Abstand von etwa 3–4 Wochen empfohlen.
        </p>
      </LegalSection>

      {/* CTA */}
      <LegalSection>
        <div className="bg-gradient-to-br from-[#1A1A1A] via-[#173c40] to-[#1A1A1A] text-white p-10 md:p-14 text-center">
          <p className="text-[#C5A059] text-sm tracking-[0.3em] uppercase mb-4">
            Ihre Behandlung bei Urban Beauty Klosters
          </p>
          <p className="text-white/80 max-w-xl mx-auto mb-3 leading-relaxed">
            Jede Haut ist anders. Deshalb wird vor der ersten Behandlung der
            Hautzustand beurteilt und die Behandlung individuell an Ihre Haut und
            Ihr persönliches Behandlungsziel angepasst.
          </p>
          <p className="font-cormorant italic text-2xl md:text-3xl mb-8">
            Natürlich. Individuell. Professionell.
          </p>
          <a
            href={FRESHA_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="laser-hv-cta-bottom"
            className="inline-block bg-[#C5A059] text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wider uppercase font-medium hover:bg-[#d4b06a] transition-colors duration-300"
          >
            Jetzt Termin buchen
          </a>
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
